import * as React from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import { Button, TextField, MenuItem, FormControl, Grid } from '@material-ui/core';
import { AssessmentTypes } from '../../Assessments';
import { Subjects, Indicators, Grades, getPairedCampus } from '../+store/aToF/aToF.constants';
import MultiSelectControl from './MultiSelect.component';
import { UserTypes } from '../../common';
import { getCurrentYear, getSchoolYearsModel } from '../../common/+store/utils';
import { JobNames } from '../../FlightPlan/+store/flightPlan';
import CircularSelect from './CircularSelect.component';

// TEA pairing (a K-2 campus inherits its feeder's STAAR rating) only applies to state
// assessment runs. Interim/CBA groups carry the K-2 campus's own tests, so it reports as
// itself. Mirrors the isInterim routing in aToF.epics.
const INTERIM_TYPES = [AssessmentTypes.Interim, AssessmentTypes.ComboAssessment, AssessmentTypes.DMACInterim];
const isInterimRun = (assessmentsInfo) => (assessmentsInfo || []).some((a) => a && INTERIM_TYPES.includes(a.type));
const resolvePairedCampus = (school, schoolYear, assessmentsInfo) =>
  isInterimRun(assessmentsInfo) ? null : getPairedCampus(school, schoolYear);

// A report-capable selection: a 9-digit campus or a campus group. (The
// 6-digit district entry is the only other selectable value.)

// Restore the last-used filters from localStorage only when they still apply: same school year as
// the current one (so the page always opens on the current year instead of whatever was run last)
// and a campus that exists in this org's school list (so a browser last used for another district
// does not inject that district's campus/assessment ids). Otherwise fall back to defaults.
const readCachedFilters = (schoolList) => {
  let cached = null;
  try {
    cached = JSON.parse(localStorage.getItem('aToFFilters'));
  } catch (e) {
    return null;
  }
  if (!cached || cached.schoolYear !== getCurrentYear()) return null;
  if (!schoolList.some(([campusNumber]) => campusNumber === cached.school)) return null;
  return cached;
};

const isCampusSelection = (school) => /^\d{9}$/.test(school) || String(school).startsWith('group:');
const isCampusGroupSelection = (school) => String(school).startsWith('group:');

const styles = (theme) => ({
  root: {
    marginRight: 8,
  },
  menu: {},
  textField: {
    width: '100%',
  },
  formControl: {
    minWidth: 218,
    marginRight: 8,
  },
  filterControl: {
    minWidth: 158,
    marginRight: 8,
  },
  multiSelectFormControl: {
    marginRight: 8,
    marginTop: 16,
    marginBottom: 8,
    minWidth: 218,
  },
  multiSelectFilterControl: {
    marginRight: 8,
    marginTop: 16,
    marginBottom: 8,
    minWidth: 158,
  },
  warningText: {
    color: 'rgba(0, 0, 0, 0.54)',
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  chip: {
    margin: theme.spacing.unit / 4,
  },
  filterRow: {
    display: 'flex',
    flexDirection: 'row',
  },
});

const schoolYears = getSchoolYearsModel();
// const schoolYears = {
//   2022: '2021-2022',
//   2021: '2020-2021',
//   2020: '2019-2020',
//   2019: '2018-2019',
// };

const scoreType = {
  average: 'Average',
  best: 'Best',
  mostRecent: 'Most Recent',
};

const makeAssessmentInfo = (assessmentSource, assessmentList) => {
  let assessmentsInfo = [];
  if (assessmentSource) {
    assessmentsInfo = assessmentSource
      .map((aso) => assessmentList.find((a) => a.id === aso))
      .filter((a) => !!a)
      .map((a) => ({
        id: a.id,
        type: a.type,
      }));
  }
  return assessmentsInfo;
};

class AtoFFilters extends React.PureComponent {
  static propTypes = {
    classes: PropTypes.any.isRequired,
    status: PropTypes.shape({
      error: PropTypes.string,
      message: PropTypes.string,
    }).isRequired,
    getReport: PropTypes.func.isRequired,
    assessmentList: PropTypes.array.isRequired,
    schoolList: PropTypes.array.isRequired,
    filters: PropTypes.object.isRequired,
    updateFilters: PropTypes.func.isRequired,
    setReportStale: PropTypes.func,
    setLastRun: PropTypes.func,
    lastRun: PropTypes.object,
    // filterFields: PropTypes.object.isRequired,
    getFilterFields: PropTypes.func.isRequired,
    filterFields: PropTypes.object,
    reportError: PropTypes.any,
    waitMessage: PropTypes.any,
    assessmentGroups: PropTypes.object.isRequired,
    profile: PropTypes.object.isRequired,
    ignoreCache: PropTypes.bool.isRequired,
    ignoreTests: PropTypes.bool.isRequired,
    debug: PropTypes.bool.isRequired,
    jobName: PropTypes.string.isRequired,
    orientation: PropTypes.string,
  };

  state = {
    option: null,
    isUpdated: true,
    submitError: null,
    // isFilterFetched: false,
  };
  filterFieldParams = '';
  componentDidMount() {
    const { schoolList, filters, updateFilters, profile, campusGroups, getCampusGroups } = this.props;
    // Lazy-load the org's campus groups for flagged district admins so the
    // picker section can render (spec §7b — everyone else fetches nothing).
    if (
      getCampusGroups &&
      campusGroups == null &&
      profile &&
      profile.role === 'districtAdmin' &&
      profile.featureFlags &&
      profile.featureFlags.campusGroups === true
    ) {
      getCampusGroups();
    }
    const { school } = filters;
    if (schoolList.length > 0 && !school) {
      const cachedFilters = readCachedFilters(schoolList);
      if (cachedFilters) {
        updateFilters(Object.assign({}, cachedFilters));
        this.syncCombineOption(cachedFilters.assessmentSource, cachedFilters.assessmentType);
      } else {
        updateFilters(
          Object.assign({}, filters, {
            school: schoolList[0][0],
            schoolYear: getCurrentYear(),
            assessmentType: 'individual',
          })
        );
      }
    }
  }

  componentDidUpdate(prevProps) {
    this.restoreOptionOnCancel(prevProps);
    const { schoolList, filters, updateFilters } = this.props;
    const { school } = filters;
    if (schoolList.length > 0 && !school) {
      const cachedFilters = readCachedFilters(schoolList);
      if (cachedFilters) {
        updateFilters(
          Object.assign({}, cachedFilters, {
            subject: ['all'],
            grade: ['all'],
            teacher: ['all'],
            studentGroup: ['all'],
            tag: ['all'],
            columnTags: [],
          })
        );
        this.syncCombineOption(cachedFilters.assessmentSource, cachedFilters.assessmentType);
      } else {
        updateFilters(
          Object.assign({}, filters, {
            school: schoolList[0][0],
            schoolYear: getCurrentYear(),
            assessmentType: 'individual',
            subject: ['all'],
            grade: ['all'],
            teacher: ['all'],
            studentGroup: ['all'],
            tag: ['all'],
            columnTags: [],
          })
        );
      }
    }
  }

  // Every filter change funnels through here. `isUpdated` brings the Get Report button back;
  // the stale flag tells the report pages to swap the report body for a prompt, so nobody
  // reads last run's numbers under this run's filters.
  markFiltersChanged = () => {
    const { setReportStale } = this.props;
    this.setState({ isUpdated: true });
    if (setReportStale) {
      setReportStale(true);
    }
  };

  // Cancel on that prompt restores the filters through redux, but the score option lives in
  // this component's state, so it has to come back from the snapshot too. The token tells
  // one Cancel from the next. Called from componentDidUpdate above — this class already has
  // one, and a second definition would silently replace it.
  restoreOptionOnCancel = (prevProps) => {
    const { lastRun } = this.props;
    const prevToken = (prevProps.lastRun && prevProps.lastRun.restoreToken) || 0;
    const token = (lastRun && lastRun.restoreToken) || 0;
    if (token !== prevToken) {
      this.setState({ option: lastRun ? lastRun.option : null, isUpdated: false });
    }
  };

  handleSelectTeacher = (teacher) => {
    const { filters, updateFilters } = this.props;
    const updatedFilters = Object.assign({}, filters, {
      teacher: [teacher],
    });
    if (JSON.stringify(updatedFilters) !== JSON.stringify(filters)) {
      updateFilters(updatedFilters);
      this.markFiltersChanged();
    }
  };
  handleSelectTag = (tag) => {
    const { filters, updateFilters } = this.props;
    const updatedFilters = Object.assign({}, filters, {
      tag: [tag],
    });
    if (JSON.stringify(updatedFilters) !== JSON.stringify(filters)) {
      updateFilters(updatedFilters);
      this.markFiltersChanged();
    }
  };
  // "Show tag(s) as columns" — a plain multi-select of specific tag names (no
  // "all" collapsing like the other filters). Each chosen tag becomes an extra
  // far-right subset column on the report; it never narrows the report.
  handleColumnTagsChange = (event) => {
    const { filters, updateFilters } = this.props;
    let { value } = event.target;
    value = Array.isArray(value) ? value.filter((v) => v !== 'all') : [];
    const updatedFilters = Object.assign({}, filters, { columnTags: value });
    if (JSON.stringify(updatedFilters) !== JSON.stringify(filters)) {
      updateFilters(updatedFilters);
      this.markFiltersChanged();
    }
  };

  renderTeacherInstantSelector = () => {
    const { filters, filterFields } = this.props;
    const { teacher } = filters;
    let { teachers } = filterFields;
    teachers = teachers.length !== 0 ? [{ value: 'all', label: 'All Teachers' }, ...teachers] : teachers;
    if (!!teachers.length && teacher && teacher.length === 1 && teacher[0] !== 'all') {
      return <CircularSelect options={teachers} selectedOption={teacher[0]} onSelect={this.handleSelectTeacher} />;
    }
    return null;
  };
  renderTagInstantSelector = () => {
    const { filters, filterFields } = this.props;
    const { tag } = filters;
    let { tags } = filterFields;
    tags = tags.length !== 0 ? [{ value: 'all', label: 'All Tags' }, ...tags] : tags;
    if (!!tags.length && tag && tag.length === 1 && tag[0] !== 'all') {
      return <CircularSelect options={tags} selectedOption={tag[0]} onSelect={this.handleSelectTag} />;
    }
    return null;
  };

  // Persistent unofficial banner for campus-group selections (spec §7b).
  // Keys off the shared filters.school value so it covers both the A-F and
  // Flight Plan views, before and after the report loads.
  renderCampusGroupBanner = () => {
    const { classes, filters, campusGroups, reportData } = this.props;
    const { school } = filters;
    if (!school || !isCampusGroupSelection(school)) return null;
    const groupId = String(school).slice('group:'.length);
    const group = (campusGroups || {})[groupId];
    const memberCount = group
      ? new Set((group.memberSegments || []).map((segment) => segment.campusNumber)).size
      : null;
    // Which D3 prior-year treatment the loaded report actually used (only
    // known once the report is back; the aToF job stores it in reportData).
    // Guarded to THIS selection so a stale report for another campus can't
    // mislabel the current group.
    const reportMatchesSelection = reportData && reportData.districtOrCampusNumber === school;
    const treatment = reportMatchesSelection && reportData.report ? reportData.report.d3PriorYearTreatment : null;
    const treatmentLine =
      treatment === 'recorded'
        ? " Closing the Gaps uses the mirrored campus's recorded prior-year data."
        : treatment === 'firstYear'
        ? ' Closing the Gaps is scored the way TEA scores a first-year campus (no prior-year history).'
        : '';
    return (
      <p className={classes.warningText} style={{ fontWeight: 600 }}>
        Campus Group (unofficial) — pooled student-level calculation
        {memberCount ? ` across ${memberCount} campus${memberCount === 1 ? '' : 'es'}` : ' across member campuses'}. TEA
        does not publish ratings for campus groups.{treatmentLine}
      </p>
    );
  };

  renderFilterRow = () => {
    const { classes, filters, filterFields, profile, reportError } = this.props;

    const { assessmentSource, school, subject, grade, teacher, studentGroup, tag, columnTags, schoolYear } = filters;
    const { isUpdated } = this.state;
    let { subjects, grades, teachers, tags } = filterFields;
    const columnTagOptions = filterFields.tags || [];
    subjects = subjects.length !== 0 ? [{ value: 'all', label: 'All Subjects' }, ...subjects] : subjects;
    grades = grades.length !== 0 ? [{ value: 'all', label: 'All Grades' }, ...grades] : grades;
    teachers = teachers.length !== 0 ? [{ value: 'all', label: 'All Teachers' }, ...teachers] : teachers;
    tags = tags.length !== 0 ? [{ value: 'all', label: 'All Tags' }, ...tags] : tags;
    const updateButton = null;

    if (
      schoolYear &&
      school &&
      isCampusSelection(school) &&
      profile.userType === UserTypes.firstClass &&
      !!assessmentSource.length &&
      (subjects.length || teachers.length || grades.length)
    ) {
      return (
        <Grid container direction="row">
          {!!subjects.length && (
            <MultiSelectControl
              handleChange={this.handleChange('subject')}
              value={subject || []}
              label="Subject"
              getSelected={(s) => (s === 'all' ? 'All Subjects' : Subjects[s])}
              options={subjects}
              helpText="Filter by subject"
              formControlClass={classes.multiSelectFilterControl}
              id="aToFSubjectFilter"
            />
          )}
          {!!teachers.length && (
            <MultiSelectControl
              handleChange={this.handleChange('teacher')}
              value={teacher || []}
              label="Teacher"
              getSelected={(s) => (s === 'all' ? 'All Teachers' : s)}
              options={teachers}
              helpText="Filter by teacher"
              formControlClass={classes.multiSelectFilterControl}
              id="aToFTeacherFilter"
            />
          )}
          {!!grades.length && (
            <MultiSelectControl
              handleChange={this.handleChange('grade')}
              value={grade || []}
              label="Grade"
              getSelected={(s) => (s === 'all' ? 'All Grades' : Grades[s])}
              options={grades}
              helpText="Filter by grade"
              formControlClass={classes.multiSelectFilterControl}
              id="aToFGradeFilter"
            />
          )}
          <MultiSelectControl
            handleChange={this.handleChange('studentGroup')}
            value={studentGroup || []}
            label="Student Group"
            getSelected={(s) => Indicators[s]}
            options={Object.entries(Indicators).map(([value, label]) => ({
              value,
              label,
            }))}
            helpText="Filter by student group"
            formControlClass={classes.multiSelectFilterControl}
            id="aToFStudentGroupFilter"
          />
          {!!tags.length && (
            <MultiSelectControl
              handleChange={this.handleChange('tag')}
              value={tag || []}
              label="Student Tag"
              getSelected={(s) => (s === 'all' ? 'All Tags' : s)}
              options={tags}
              helpText="Filter by tag"
              formControlClass={classes.multiSelectFilterControl}
              id="aToFTagFilter"
            />
          )}
          {!!columnTagOptions.length && (
            <MultiSelectControl
              handleChange={this.handleColumnTagsChange}
              value={columnTags || []}
              label="Show Tag(s) as Columns"
              getSelected={(s) => s}
              options={columnTagOptions}
              helpText="Add a far-right column for each tagged subset (does not change any score)"
              formControlClass={classes.multiSelectFilterControl}
              id="aToFColumnTagsFilter"
            />
          )}
          {/* { updateButton } */}
        </Grid>
      );
    }

    return updateButton;
  };

  getFiltersFromBackend = (isInit = false) => {
    const { filters, getFilterFields, assessmentList, assessmentGroups } = this.props;
    const { assessmentSource, school, assessmentType } = filters;
    let assessmentSourceList = assessmentSource;
    if (assessmentType === 'group') {
      assessmentSourceList = assessmentSource in assessmentGroups ? assessmentGroups[assessmentSource].assessments : [];
    }
    const assessmentsInfo = makeAssessmentInfo(assessmentSourceList, assessmentList);
    if (assessmentsInfo.length && school) {
      const { subject, grade, teacher, studentGroup, tag, schoolYear } = filters;
      const afFilters = {
        subjects: subject && !isInit ? subject : [],
        grades: grade && !isInit ? grade : [],
        teachers: teacher && !isInit ? teacher : [],
        studentGroups: studentGroup && !isInit ? studentGroup : [],
        tags: tag && !isInit ? tag : [],
      };
      // Resolve a paired (non-testing) campus to its feeder so the filter
      // selectors pull from the campus that actually has tests.
      const filterCampus = resolvePairedCampus(school, schoolYear, assessmentsInfo) || school;
      const filterFieldParams = `${JSON.stringify(assessmentsInfo)}:${filterCampus}`;
      if (filterFieldParams !== this.filterFieldParams) {
        this.filterFieldParams = filterFieldParams;
        getFilterFields(schoolYear, assessmentsInfo, filterCampus, afFilters);
      }
    }
  };

  onSubmit = () => {
    const {
      getReport,
      assessmentList,
      filters,
      assessmentGroups,
      ignoreCache,
      ignoreTests,
      debug,
      jobName,
      profile,
      setLastRun,
    } = this.props;
    let { assessmentSource } = filters;
    const { school, subject, grade, teacher, studentGroup, tag, columnTags, schoolYear, assessmentType } = filters;
    const {
      option,
      // isFilterFetched,
    } = this.state;
    // Todo: add validation errors
    const afFilters = {
      subjects: subject || [],
      grades: grade || [],
      teachers: teacher || [],
      studentGroups: studentGroup || [],
      tags: tag || [],
      // Tags to render as extra far-right subset columns (no effect on calculation).
      columnTags: columnTags || [],
    };
    if (assessmentType === 'group') {
      assessmentSource = assessmentSource in assessmentGroups ? assessmentGroups[assessmentSource].assessments : [];
    }
    const assessmentsInfo = makeAssessmentInfo(assessmentSource, assessmentList);
    if (assessmentsInfo && assessmentsInfo.length > 0 && school) {
      localStorage.setItem(
        'aToFFilters',
        JSON.stringify(
          {
            school: filters.school,
            schoolYear: filters.schoolYear,
            assessmentType: filters.assessmentType,
            assessmentSource: filters.assessmentSource,
          },
          null,
          2
        )
      );
      // this.getFiltersFromBackend(!isFilterFetched);
      // A non-STAAR-tested campus (e.g. K-2) has no tests of its own; run the
      // report against the campus it's paired/feeds into so it inherits that
      // rating, per TEA pairing rules. Falls back to the selected campus.
      getReport({
        schoolYear,
        assessmentsInfo,
        school: resolvePairedCampus(school, schoolYear, assessmentsInfo) || school,
        afFilters,
        option,
        ignoreCache,
        ignoreTests,
        debug,
        jobName,
        profile,
      });
      // Remember exactly what produced this report so Cancel can put the filter bar back.
      // Snapshot the redux filters, not afFilters — afFilters is the server-shaped subset.
      if (setLastRun) {
        setLastRun(filters, option);
      }
      this.setState({
        isUpdated: false,
        submitError: null,
        // isFilterFetched: true,
      });
    } else {
      this.setState({
        submitError:
          'The report could not be started because no assessment files are available for this selection. Please contact CoPilot support.',
      });
    }
  };

  isComboOrInterim = (assessmentId) => {
    const { assessmentList } = this.props;
    const assessment = assessmentList.find((a) => a.id === assessmentId);
    // Ids missing from the current year's assessmentList (e.g. selections cached
    // from another school year) must not count as interims, or the combine
    // Options dropdown appears for what looks like a single selected assessment.
    if (!assessment) {
      return false;
    }
    return [AssessmentTypes.ComboAssessment, AssessmentTypes.Interim, AssessmentTypes.DMACInterim].includes(
      assessment.type
    );
  };

  showAssessmentCombineOption = (assessmentSourceList) =>
    Array.isArray(assessmentSourceList) ? assessmentSourceList.every(this.isComboOrInterim) : false;

  // In group mode assessmentSource is a single group id; the ids that matter for the combine
  // Options dropdown are the group's members, so an all-interim group offers the same
  // Best/Average/Most Recent choice as picking those interims individually.
  resolveSelectedAssessmentIds = (assessmentSource, assessmentType) => {
    const { assessmentGroups, assessmentList } = this.props;
    let ids = assessmentSource;
    if (assessmentType === 'group') {
      ids =
        assessmentSource && assessmentSource in assessmentGroups ? assessmentGroups[assessmentSource].assessments : [];
    }
    return Array.isArray(ids) ? ids.filter((id) => assessmentList.some((a) => a.id === id)) : [];
  };

  // A group carries the score option it was created with, so a set of sequential CBAs keeps
  // averaging every time it is opened rather than reverting to Best. Groups saved before the
  // setting existed have none and keep today's behavior. Picking loose assessments (no group)
  // still starts at Best. Changing the dropdown afterwards only affects that run — nothing
  // here writes back to the group.
  groupScoreOption = (assessmentSource, assessmentType) => {
    const { assessmentGroups } = this.props;
    if (assessmentType !== 'group') {
      return null;
    }
    const group = assessmentSource && assessmentSource in assessmentGroups ? assessmentGroups[assessmentSource] : null;
    return (group && group.scoreOption) || null;
  };

  syncCombineOption = (assessmentSource, assessmentType) => {
    const selected = this.resolveSelectedAssessmentIds(assessmentSource, assessmentType);
    const combinable = selected.length && this.showAssessmentCombineOption(selected);
    this.setState({
      option: combinable ? this.groupScoreOption(assessmentSource, assessmentType) || 'best' : null,
    });
  };

  filterOutComboOrStaar = (selectedAssessmentList) => {
    if (Array.isArray(selectedAssessmentList)) {
      const currentAssessmentId = selectedAssessmentList[selectedAssessmentList.length - 1];
      const isComboOrInterim = this.isComboOrInterim(currentAssessmentId);
      return selectedAssessmentList.filter((id) => isComboOrInterim === this.isComboOrInterim(id));
    }
    return selectedAssessmentList;
  };

  handleChange = (name) => (event) => {
    const { filters, updateFilters, filterFields } = this.props;
    const { subjects, grades, teachers, tags } = filterFields;
    if (this.state.submitError) {
      this.setState({ submitError: null });
    }
    if (event.target.value === 'No Selection') {
      event.target.value = null;
    }
    let updatedFilters = filters;
    let filteredAssessmentSourceList;
    switch (name) {
      case 'school':
        updatedFilters = Object.assign({}, filters, {
          school: event.target.value,
          schoolYear: filters.schoolYear || getCurrentYear(),
          assessmentType: filters.assessmentType || 'individual',
          assessmentSource: filters.assessmentSource || [],
          subject: filters.subject || ['all'],
          grade: filters.grade || ['all'],
          teacher: filters.teacher || ['all'],
          studentGroup: filters.studentGroup || ['all'],
          tag: filters.tag || ['all'],
        });
        break;
      case 'schoolYear':
        updatedFilters = Object.assign({}, filters, {
          schoolYear: event.target.value,
          assessmentType: 'individual',
          assessmentSource: [],
          subject: ['all'],
          grade: ['all'],
          teacher: ['all'],
          studentGroup: ['all'],
          tag: ['all'],
          columnTags: [],
        });
        break;
      case 'assessmentType':
        this.setState({ option: null });
        updatedFilters = Object.assign({}, filters, {
          assessmentType: event.target.value,
          assessmentSource: event.target.value === 'group' ? '' : [],
          subject: ['all'],
          grade: ['all'],
          teacher: ['all'],
          studentGroup: ['all'],
          tag: ['all'],
          columnTags: [],
        });
        break;
      case 'assessmentSource':
        filteredAssessmentSourceList = this.filterOutComboOrStaar(event.target.value);
        updatedFilters = Object.assign({}, filters, {
          assessmentSource: filteredAssessmentSourceList,
          subject: ['all'],
          grade: ['all'],
          teacher: ['all'],
          studentGroup: ['all'],
          tag: ['all'],
          columnTags: [],
        });
        this.syncCombineOption(filteredAssessmentSourceList, filters.assessmentType);
        break;
      case 'option':
        const option = event.target.value;
        if (this.state.option !== option) {
          this.markFiltersChanged();
        }
        this.setState({ option });
        break;
      default: {
        let { value } = event.target;
        const { length } = value;
        let allOptionsLength = 0;
        if (name === 'studentGroup') {
          allOptionsLength = Object.keys(Indicators).length - 1;
        } else if (name === 'tag') {
          allOptionsLength = Object.keys(tags).length;
        } else if (name === 'teacher') {
          allOptionsLength = Object.keys(teachers).length;
        } else if (name === 'grade') {
          allOptionsLength = Object.keys(grades).length;
        } else if (name === 'subject') {
          allOptionsLength = Object.keys(subjects).length;
        }
        if (value.includes('all') && value[length - 1] !== 'all') {
          value = value.filter((e) => e !== 'all');
        } else if (value[length - 1] === 'all' || length === allOptionsLength) {
          value = ['all'];
        }
        updatedFilters = Object.assign({}, filters, {
          [name]: value,
        });
      }
    }
    if (JSON.stringify(updatedFilters) !== JSON.stringify(filters)) {
      updateFilters(updatedFilters);
      this.markFiltersChanged();
    }
  };

  render() {
    const {
      classes,
      assessmentList,
      schoolList,
      filters,
      reportError,
      waitMessage,
      assessmentGroups,
      orientation,
      jobName,
      profile,
    } = this.props;

    const dateFormat = 'MMM D, YYYY';
    const { assessmentSource, school, schoolYear, assessmentType } = filters;
    const { option, isUpdated } = this.state;
    // A non-STAAR-tested campus (e.g. K-2) is paired with a feeder campus and
    // shows that feeder's report; note which one so the user isn't surprised.
    const pairedCampusNumber = resolvePairedCampus(
      school,
      schoolYear,
      makeAssessmentInfo(assessmentSource, assessmentList)
    );
    const pairedCampusName = pairedCampusNumber
      ? (schoolList.find(([value]) => value === pairedCampusNumber) || [])[1]
      : '';
    // Stale cached selections from another school year linger in assessmentSource
    // but render as blank entries; only ids in the current year's list count when
    // deciding whether the combine Options dropdown applies.
    const selectedKnownAssessments = this.resolveSelectedAssessmentIds(assessmentSource, assessmentType);
    let assessmentSources = [];

    // Super-admin-hidden sets are excluded from the individual pickers but stay
    // in assessmentList so onSubmit can resolve them out of a visible group.
    const selectableAssessmentList = assessmentList.filter((a) => !a.hiddenFromUser);
    if (assessmentType === 'individual') {
      if (schoolYear === '2018') {
        assessmentSources = selectableAssessmentList
          .filter((a) => [AssessmentTypes.CAF2018, AssessmentTypes.DMAC2018].includes(a.type))
          .map((source) => ({
            value: source.id,
            label: source.name,
            administrationDate: source.administrationDate,
            date: source.date,
          }));
      } else if (schoolYear === '2021') {
        //2021
        if (jobName === JobNames.aToF) {
          const staarAssessments = [
            AssessmentTypes.STAAR2021,
            AssessmentTypes.STAARALT2021,
            AssessmentTypes.TELPAS2021,
            AssessmentTypes.TELPASALT2021,
            AssessmentTypes.EOC2021,
            AssessmentTypes.EOCALT2021,
          ];
          assessmentSources = selectableAssessmentList
            .filter((a) => staarAssessments.includes(a.type))
            .map((source) => ({
              value: source.id,
              label: source.name,
              administrationDate: source.administrationDate,
              date: source.date,
            }));
        }
        const interimSources = selectableAssessmentList
          .filter((a) => {
            if (
              [AssessmentTypes.ComboAssessment, AssessmentTypes.Interim, AssessmentTypes.DMACInterim].includes(a.type)
            ) {
              if (
                moment(a.administrationDate || a.date).isBefore(moment('2021-07-01')) &&
                moment(a.administrationDate || a.date).isAfter(moment('2019-07-01'))
              ) {
                return true;
              }
            }
            return false;
          })
          .map((source) => ({
            value: source.id,
            label: `${source.name} (${moment(source.administrationDate || source.date).format(dateFormat)})`,
            administrationDate: source.administrationDate,
            date: source.date,
          }));
        interimSources.forEach((s) => {
          assessmentSources.push(s);
        });
      } else if (schoolYear === '2019') {
        if (jobName === JobNames.aToF) {
          const staarAssessments = [
            AssessmentTypes.STAAR2019,
            AssessmentTypes.STAARALT2019,
            AssessmentTypes.TELPAS2019,
            AssessmentTypes.TELPASALT2019,
            AssessmentTypes.EOC2019,
            AssessmentTypes.EOCALT2019,
          ];
          assessmentSources = selectableAssessmentList
            .filter((a) => staarAssessments.includes(a.type))
            .map((source) => ({
              value: source.id,
              label: source.name,
              administrationDate: source.administrationDate,
              date: source.date,
            }));
        }
        const interimSources = selectableAssessmentList
          .filter((a) => {
            if (
              [AssessmentTypes.ComboAssessment, AssessmentTypes.Interim, AssessmentTypes.DMACInterim].includes(a.type)
            ) {
              if (
                moment(a.administrationDate || a.date).isBefore(moment('2019-07-01')) &&
                moment(a.administrationDate || a.date).isAfter(moment('2018-07-01'))
              ) {
                return true;
              }
            }
            return false;
          })
          .map((source) => ({
            value: source.id,
            label: `${source.name} (${moment(source.administrationDate || source.date).format(dateFormat)})`,
            administrationDate: source.administrationDate,
            date: source.date,
          }));
        interimSources.forEach((s) => {
          assessmentSources.push(s);
        });
      } else {
        if (jobName === JobNames.aToF) {
          const staarAssessments = [
            AssessmentTypes.STAAR + schoolYear,
            AssessmentTypes.STAARALT + schoolYear,
            AssessmentTypes.TELPAS + schoolYear,
            AssessmentTypes.TELPASALT + schoolYear,
            AssessmentTypes.EOC + schoolYear,
            AssessmentTypes.EOCALT + schoolYear,
          ];
          assessmentSources = selectableAssessmentList
            .filter((a) => staarAssessments.includes(a.type))
            .map((source) => ({
              value: source.id,
              label: source.name,
              administrationDate: source.administrationDate,
              date: source.date,
            }));
        }
        const interimSources = selectableAssessmentList
          .filter((a) => {
            if (
              [AssessmentTypes.ComboAssessment, AssessmentTypes.Interim, AssessmentTypes.DMACInterim].includes(a.type)
            ) {
              if (
                moment(a.administrationDate || a.date).isBefore(moment(schoolYear + '-07-01')) &&
                moment(a.administrationDate || a.date).isAfter(moment(schoolYear - 1 + '-07-01'))
              ) {
                return true;
              }
            }
            return false;
          })
          .map((source) => ({
            value: source.id,
            label: `${source.name} (${moment(source.administrationDate || source.date).format(dateFormat)})`,
            administrationDate: source.administrationDate,
            date: source.date,
          }));
        interimSources.forEach((s) => {
          assessmentSources.push(s);
        });
      }
      ///
    } else if (assessmentType === 'group') {
      assessmentSources = Object.entries(assessmentGroups)
        .filter(([, group]) => group.year === schoolYear)
        .map(([groupId, source]) => ({
          value: groupId,
          label: source.name,
          administrationDate: source.administrationDate,
          date: source.date,
        }));
    }

    // sort list
    if (assessmentSources != null) {
      assessmentSources = assessmentSources.sort(
        (a, b) => moment(b.administrationDate || b.date) - moment(a.administrationDate || a.date)
      );
      // Sort descending (latest first)
      // assessmentSources = assessmentSources.sort(function(a, b) {
      //   return a.label.localeCompare(b.label);
      // });
    }

    let updateButton = null;
    if ((schoolYear && !!assessmentSource.length && school && isUpdated) || reportError) {
      updateButton = (
        <Button
          color="primary"
          variant="contained"
          style={{
            marginTop: orientation === 'horizontal' ? 16 : 0,
            height: '56px',
            width: '200px',
          }}
          onClick={this.onSubmit}
        >
          Get Report
        </Button>
      );
    }

    return (
      <form
        className={classes.root}
        onSubmit={this.onSubmit}
        style={{
          flexDirection: orientation === 'horizontal' ? 'row' : 'column',
          width: orientation === 'horizontal' ? '' : '100%',
        }}
      >
        {orientation === 'vertical' && updateButton}
        <Grid container direction="column" justify="center" alignItems="flex-start">
          <div
            className={classes.filterRow}
            style={{
              width: orientation === 'horizontal' ? '' : '100%',
            }}
          >
            <Grid container direction="row">
              <FormControl className={classes.formControl}>
                <TextField
                  select
                  label="School"
                  className={classes.textField}
                  value={school || ''}
                  onChange={this.handleChange('school')}
                  SelectProps={{
                    MenuProps: {
                      className: classes.menu,
                    },
                  }}
                  helperText={
                    pairedCampusName
                      ? `Paired campus — showing ${pairedCampusName}'s report (K-2 campus, not STAAR-tested)`
                      : 'Select a school'
                  }
                  margin="normal"
                >
                  {/* Group entries are styled inline rather than under a
                      disabled header MenuItem — MUI v3's SelectInput fires
                      onChange for disabled children activated by keyboard,
                      which would select undefined and wipe the choice. */}
                  {schoolList.map(([value, label, kind]) => (
                    <MenuItem
                      key={value}
                      value={value}
                      style={kind === 'campusGroup' ? { fontStyle: 'italic' } : undefined}
                    >
                      {kind === 'campusGroup' ? `${label} (Campus Group)` : label}
                    </MenuItem>
                  ))}
                </TextField>
              </FormControl>
              {school && (
                <FormControl className={classes.formControl}>
                  <TextField
                    select
                    label="School Year"
                    className={classes.textField}
                    value={schoolYear || ''}
                    onChange={this.handleChange('schoolYear')}
                    SelectProps={{
                      MenuProps: {
                        className: classes.menu,
                      },
                    }}
                    helperText="Please select a school year"
                    margin="normal"
                  >
                    {Object.entries(schoolYears).map(([value, label]) => (
                      <MenuItem key={value} value={value}>
                        {label}
                      </MenuItem>
                    ))}
                  </TextField>
                </FormControl>
              )}
              {schoolYear && (
                <FormControl className={classes.formControl}>
                  <TextField
                    select
                    label="Assessment Source Type"
                    className={classes.textField}
                    value={assessmentType || ''}
                    onChange={this.handleChange('assessmentType')}
                    SelectProps={{
                      MenuProps: {
                        className: classes.menu,
                      },
                    }}
                    helperText="Please select an assessment source type"
                    margin="normal"
                  >
                    <MenuItem key="group" value="group">
                      A Custom Assessment Group
                    </MenuItem>
                    <MenuItem key="individual" value="individual">
                      Individual Assessment(s)
                    </MenuItem>
                  </TextField>
                </FormControl>
              )}
              {assessmentType === 'individual' && (
                <FormControl className={classes.formControl}>
                  <MultiSelectControl
                    id="aToFAssessmentSourceFilter"
                    handleChange={this.handleChange('assessmentSource')}
                    value={assessmentSource || []}
                    label="Individual Assessment Source"
                    getSelected={(s) => {
                      const a = assessmentList.find((a) => a.id === s);
                      return a ? a.name : '';
                    }}
                    options={assessmentSources}
                    helpText="Please select one or more assessment sources"
                    formControlClass={classes.multiSelectFormControl}
                  >
                    {!assessmentSources.length && (
                      <MenuItem disabled value="">
                        {`None available for ${schoolYears[schoolYear]}`}
                      </MenuItem>
                    )}
                  </MultiSelectControl>
                </FormControl>
              )}
              {selectedKnownAssessments.length >= 2 && this.showAssessmentCombineOption(selectedKnownAssessments) && (
                <FormControl className={classes.formControl} style={{ width: '20%' }}>
                  <TextField
                    select
                    label="Options"
                    className={classes.textField}
                    value={option || 'best'}
                    onChange={this.handleChange('option')}
                    SelectProps={{
                      MenuProps: {
                        className: classes.menu,
                      },
                    }}
                    helperText="Select which test should be included in the report for students with multiple tests in the same subject"
                    margin="normal"
                  >
                    {Object.entries(scoreType).map(([value, label]) => (
                      <MenuItem key={value} value={value}>
                        {label}
                      </MenuItem>
                    ))}
                  </TextField>
                </FormControl>
              )}
              {assessmentType === 'group' && (
                <FormControl className={classes.formControl}>
                  <TextField
                    select
                    label="Group Assessment Source"
                    className={classes.textField}
                    value={assessmentSource || ''}
                    onChange={this.handleChange('assessmentSource')}
                    SelectProps={{
                      MenuProps: {
                        className: classes.menu,
                      },
                    }}
                    helperText="Please select one group assessment source"
                    margin="normal"
                  >
                    {assessmentSources.map((source) => (
                      <MenuItem key={source.value} value={source.value}>
                        {source.label}
                      </MenuItem>
                    ))}
                    {!assessmentSources.length && (
                      <MenuItem disabled value="">
                        {`None available for ${schoolYears[schoolYear]}`}
                      </MenuItem>
                    )}
                  </TextField>
                </FormControl>
              )}
            </Grid>
          </div>
          {this.renderFilterRow()}
          {((schoolYear && !!assessmentSource.length && school) || reportError) &&
            this.renderTeacherInstantSelector() &&
            this.renderTagInstantSelector()}
          {orientation === 'horizontal' && updateButton}
          {this.renderCampusGroupBanner()}
          {this.state.submitError && <p className={classes.warningText}> {this.state.submitError} </p>}
          {reportError && <p className={classes.warningText}> {reportError} </p>}
          {waitMessage && <p className={classes.warningText}> {waitMessage} </p>}
        </Grid>
      </form>
    );
  }
}

export default withStyles(styles)(AtoFFilters);
